import React, { useEffect, useState } from 'react';
import { Drawer } from '../../components/ui/Drawer';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { transactionsApi, TransactionDetail as TxDetail } from '../../api/transactions';

interface TransactionDetailProps {
  transactionId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const statusColors: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  success: 'success',
  failed: 'danger',
  pending: 'warning',
  reversed: 'neutral',
  scheduled: 'warning',
};

const typeColors: Record<string, 'info' | 'success' | 'neutral'> = {
  debit: 'info',
  credit: 'success',
  reversal: 'neutral',
};

const formatNaira = (amount: string | number) => {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '140px 1fr',
      gap: 12,
      padding: '10px 0',
      borderBottom: '1px solid var(--neutral-200)',
      alignItems: 'center',
    }}
  >
    <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>{label}</span>
    <div style={{ fontSize: 13, color: 'var(--neutral-800)' }}>{children}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: 'var(--neutral-500)',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginTop: 24,
      marginBottom: 8,
    }}
  >
    {children}
  </h3>
);

export const TransactionDetail: React.FC<TransactionDetailProps> = ({
  transactionId,
  isOpen,
  onClose,
}) => {
  const [tx, setTx] = useState<TxDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !transactionId) {
      setTx(null);
      return;
    }
    const fetchTx = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await transactionsApi.getTransactionById(transactionId);
        setTx(data);
      } catch {
        setError('Failed to load transaction details.');
      } finally {
        setLoading(false);
      }
    };
    void fetchTx();
  }, [isOpen, transactionId]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={tx ? `${formatNaira(tx.amount)} ${tx.type}` : 'Loading…'}
      subtitle={tx?.reference}
      width={600}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner size={24} />
        </div>
      ) : error ? (
        <p style={{ color: 'var(--danger-600)', textAlign: 'center', padding: 24 }}>{error}</p>
      ) : tx ? (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <Badge variant={statusColors[tx.status]}>{tx.status}</Badge>
            <Badge variant={typeColors[tx.type]}>{tx.type}</Badge>
          </div>

          <SectionTitle>Transaction</SectionTitle>
          <InfoRow label="Reference">
            <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              {tx.reference}
            </span>
          </InfoRow>
          {tx.providerReference && (
            <InfoRow label="External Ref">
              <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                {tx.providerReference}
              </span>
            </InfoRow>
          )}
          <InfoRow label="Amount">
            <span style={{ fontWeight: 600, fontSize: 14 }}>{formatNaira(tx.amount)}</span>
          </InfoRow>
          <InfoRow label="Fee">{formatNaira(tx.totalFee)}</InfoRow>
          <InfoRow label="Created">{formatDateTime(tx.createdAt)}</InfoRow>

          <SectionTitle>Counterparty</SectionTitle>
          <InfoRow label="Account Name">{tx.accountName}</InfoRow>
          <InfoRow label="Account Number">
            <span style={{ fontFamily: 'monospace' }}>{tx.accountNumber}</span>
          </InfoRow>
          <InfoRow label="Bank">{tx.bankName}</InfoRow>
          {tx.narration && <InfoRow label="Narration">{tx.narration}</InfoRow>}

          <SectionTitle>User</SectionTitle>
          <InfoRow label="User ID">
            <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
              {tx.user?.id ?? tx.userId}
            </span>
          </InfoRow>
          <InfoRow label="Name">
            {tx.user?.firstName || tx.user?.lastName
              ? `${tx.user.firstName ?? ''} ${tx.user.lastName ?? ''}`.trim()
              : '—'}
          </InfoRow>
          <InfoRow label="Phone">{tx.user?.phoneNumber ?? '—'}</InfoRow>
          {tx.user?.email && <InfoRow label="Email">{tx.user.email}</InfoRow>}

          {tx.failureReason && (
            <>
              <SectionTitle>Failure Reason</SectionTitle>
              <div
                style={{
                  padding: 12,
                  background: 'var(--danger-100)',
                  color: 'var(--danger-900)',
                  fontSize: 13,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--danger-300)',
                }}
              >
                {tx.failureReason}
              </div>
            </>
          )}

          {tx.metadata && Object.keys(tx.metadata).length > 0 && (
            <>
              <SectionTitle>Metadata</SectionTitle>
              <pre
                style={{
                  fontSize: 12,
                  padding: 12,
                  background: 'var(--neutral-200)',
                  borderRadius: 'var(--radius-md)',
                  overflowX: 'auto',
                  fontFamily: 'monospace',
                  color: 'var(--neutral-700)',
                  margin: 0,
                }}
              >
                {JSON.stringify(tx.metadata, null, 2)}
              </pre>
            </>
          )}

          {tx.providerResponse && Object.keys(tx.providerResponse).length > 0 && (
            <>
              <SectionTitle>Provider Response</SectionTitle>
              <pre
                style={{
                  fontSize: 12,
                  padding: 12,
                  background: 'var(--neutral-200)',
                  borderRadius: 'var(--radius-md)',
                  overflowX: 'auto',
                  fontFamily: 'monospace',
                  color: 'var(--neutral-700)',
                  margin: 0,
                }}
              >
                {JSON.stringify(tx.providerResponse, null, 2)}
              </pre>
            </>
          )}
        </>
      ) : null}
    </Drawer>
  );
};
